import React from 'react';
import { ActionIcon, Group } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import classes from './TextStyleToggles.module.css';

/* Les glyphes de la barre de texte du classique (TextStylingComponent) : des
   lettres, pas des icônes, pour gras / italique / souligné ; les alignements
   en duotone (le « Duotone Light weight solid » du classique, en npm pour
   l'iframe du canvas où le kit FontAwesome n'est pas chargé). */
const letter = (text, style) => (
  <span style={{ fontFamily: 'monospace', fontSize: 16, lineHeight: 1, ...style }}>{text}</span>
);

export const duotone = (definition) => <FontAwesomeIcon icon={definition} style={{ fontSize: 16 }} />;

export const TEXT_STYLE_GLYPHS = {
  bold: letter('G', { fontWeight: 'bold' }),
  italic: letter('I', { fontStyle: 'italic' }),
  underline: letter('S', { textDecoration: 'underline' }),
};

export const TEXT_ALIGN_GLYPHS = {
  left: duotone(icon({ name: 'align-left', family: 'duotone', style: 'solid' })),
  center: duotone(icon({ name: 'align-center', family: 'duotone', style: 'solid' })),
  justify: duotone(icon({ name: 'align-justify', family: 'duotone', style: 'solid' })),
  right: duotone(icon({ name: 'align-right', family: 'duotone', style: 'solid' })),
};

const TOGGLES = [
  { key: 'bold', label: 'Gras' },
  { key: 'italic', label: 'Italique' },
  { key: 'underline', label: 'Souligné' },
];

const ALIGNMENTS = [
  { key: 'left', label: 'Aligner à gauche' },
  { key: 'center', label: 'Centrer' },
  { key: 'justify', label: 'Justifier' },
  { key: 'right', label: 'Aligner à droite' },
];

/** Mêmes boutons que la section « Texte » de la sidebar (cf. RichTextSidebarSection). */
function FormatButtons({ buttons, glyphs, isActive, onClick }) {
  return (
    <Group gap={6} wrap="nowrap">
      {buttons.map(({ key, label }) => (
        <ActionIcon
          key={key}
          variant="default"
          className={classes.toggle}
          data-active={isActive(key) || undefined}
          aria-label={label}
          title={label}
          aria-pressed={isActive(key)}
          onClick={() => onClick(key)}
        >
          {glyphs[key]}
        </ActionIcon>
      ))}
    </Group>
  );
}

/**
 * Gras / italique / souligné d'un réglage (titre du thème, liens).
 *
 * @param {{ active: { bold?: boolean, italic?: boolean, underline?: boolean },
 *   onToggle: (key: 'bold' | 'italic' | 'underline') => void }} props
 */
export default function TextStyleToggles({ active, onToggle }) {
  return (
    <FormatButtons
      buttons={TOGGLES}
      glyphs={TEXT_STYLE_GLYPHS}
      isActive={(key) => Boolean(active[key])}
      onClick={onToggle}
    />
  );
}

/**
 * Alignement d'un réglage. `options` : les alignements qu'il admet (les
 * titres n'ont pas de justifié, comme dans le classique).
 */
export function TextAlignButtons({ value, onChange, options = ALIGNMENTS.map(({ key }) => key) }) {
  return (
    <FormatButtons
      buttons={ALIGNMENTS.filter(({ key }) => options.includes(key))}
      glyphs={TEXT_ALIGN_GLYPHS}
      isActive={(key) => value === key}
      onClick={onChange}
    />
  );
}
